/**
 * Admin page for photo management
 */

import { useState } from 'react';
import { ChevronLeft, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RateLimitBadge } from '@/components/admin/RateLimitBadge';
import { PhotoManager } from '@/components/admin/PhotoManager';
import { SeedingPanel } from '@/components/admin/SeedingPanel';

const ADMIN_PASSWORD = import.meta.env.VITE_ADMIN_PASSWORD || '';
const AUTH_STORAGE_KEY = 'witw-admin-auth';

interface AdminPageProps {
  onBack: () => void;
}

export function AdminPage({ onBack }: AdminPageProps) {
  const [authenticated, setAuthenticated] = useState(() => {
    return sessionStorage.getItem(AUTH_STORAGE_KEY) === 'true';
  });
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      sessionStorage.setItem(AUTH_STORAGE_KEY, 'true');
      setError('');
    } else {
      setError('Incorrect password');
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <form onSubmit={handleLogin} className="w-full max-w-sm space-y-4">
          <div className="flex flex-col items-center gap-2 mb-6">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
              <Lock className="w-6 h-6 text-muted-foreground" />
            </div>
            <h1 className="font-display text-xl">Admin Access</h1>
          </div>
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="off"
            autoFocus
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full">
            Enter
          </Button>
          <Button type="button" variant="ghost" className="w-full" onClick={onBack}>
            Back
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-display text-xl sm:text-2xl">Admin - Photo Manager</h1>
              <p className="text-xs text-muted-foreground hidden sm:block">
                Manage country photos and Unsplash seeding
              </p>
            </div>
          </div>
          <RateLimitBadge />
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs defaultValue="seeding" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="seeding">Bulk Seeding</TabsTrigger>
            <TabsTrigger value="photos">Photo Manager</TabsTrigger>
          </TabsList>

          <TabsContent value="seeding" className="space-y-6">
            <SeedingPanel />
          </TabsContent>

          <TabsContent value="photos" className="space-y-6">
            <PhotoManager />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
